/**
 * Operational exception detail + lifecycle actions (OS Buddy — issue #1097 PR2).
 *
 * Opens one case from the queue: full evidence, the assignment, the append-only
 * audit history, and the status/permission-gated transitions (claim, release,
 * reassign, resolve, reopen). Every mutation carries the case `version` for
 * optimistic concurrency; a 409 is surfaced and the detail is re-fetched so the
 * foreman retries against fresh state (design §9).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ApiError } from '@/lib/api';
import { useAssistantStore } from '@/stores/assistantStore';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import { useStaffUsers } from '@/hooks/useStaffUsers';
import { describeApiError } from '@/lib/describeApiError';
import {
  useClaimException,
  useExceptionDetail,
  useReassignException,
  useReleaseException,
  useReopenException,
  useResolveException,
  type ExceptionStatus,
  type ExceptionUrgency,
} from '@/hooks/useExceptions';
import { EmailDeliveryEvidence } from './EmailDeliveryEvidence';

const URGENCY_COLOR: Record<ExceptionUrgency, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  high: 'warning',
  normal: 'info',
  low: 'default',
};

/** Statuses in which the case is actively owned and can be released/resolved. */
const OWNED_STATUSES: ReadonlySet<ExceptionStatus> = new Set([
  'claimed',
  'escalated',
  'reassigned',
  'stale',
]);

/** Statuses in which an unassigned case can be claimed. */
const CLAIMABLE_STATUSES: ReadonlySet<ExceptionStatus> = new Set(['open', 'released', 'reopened']);

type Mode = 'view' | 'reassign' | 'resolve' | 'reopen';

export function ExceptionDetailDialog({ exceptionId, onClose }: { exceptionId: string | null; onClose: () => void }) {
  const { t } = useTranslation('exceptions');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const openException = useAssistantStore((s) => s.openException);

  const { data, isLoading, isError, refetch } = useExceptionDetail(exceptionId);
  const { data: staff } = useStaffUsers();

  const claim = useClaimException();
  const release = useReleaseException();
  const reassign = useReassignException();
  const resolve = useResolveException();
  const reopen = useReopenException();

  const [mode, setMode] = useState<Mode>('view');
  const [assignee, setAssignee] = useState('');
  const [rationale, setRationale] = useState('');
  const [reason, setReason] = useState('');
  const [chosenKind, setChosenKind] = useState('');

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of staff?.users ?? []) map.set(u.id, `${u.firstName} ${u.lastName}`.trim());
    return map;
  }, [staff]);

  const exception = data?.exception;
  const busy = claim.isPending || release.isPending || reassign.isPending || resolve.isPending || reopen.isPending;

  const resetForms = () => {
    setMode('view');
    setAssignee('');
    setRationale('');
    setReason('');
    setChosenKind('');
  };

  const handleClose = () => {
    resetForms();
    onClose();
  };

  /** A 409 means the world moved (someone else claimed / a stale version). Show why and re-sync. */
  const onError = (err: unknown) => {
    if (err instanceof ApiError && err.status === 409) {
      const reasonCode = (err.data as { reason?: string } | undefined)?.reason ?? 'invalid_state';
      notify.error(t('conflict.title'), t(`conflict.${reasonCode}`, { defaultValue: t('conflict.invalid_state') }));
      void refetch();
      resetForms();
      return;
    }
    notify.error(tc('error'), describeApiError(err, tc, { fallback: tc('errors.generic') }));
  };

  const onDone = (titleKey: string) => {
    notify.success(t(titleKey));
    resetForms();
  };

  if (!exceptionId) return null;

  const version = exception?.version ?? 0;
  const isOwner = !!exception && !!currentUserId && exception.assignedTo === currentUserId;
  const canResolve = hasPermission('scheduling.exceptions.resolve');
  const canOverride = hasPermission('scheduling.exceptions.override');

  const showClaim = !!exception && CLAIMABLE_STATUSES.has(exception.status) && !exception.assignedTo;
  const showOwnerActions = !!exception && OWNED_STATUSES.has(exception.status);
  const showResolve = showOwnerActions && canResolve && (isOwner || canOverride);
  const showRelease = showOwnerActions && isOwner;
  const showReassign = showOwnerActions; // any foreman may hand the case to someone else
  const showReopen = !!exception && exception.status === 'resolved' && canOverride;

  const resolutionChoices: { kind: string; label: string }[] = [
    ...(exception?.resolutionOptions ?? []).map((o) => ({ kind: o.kind, label: t(`resolution.${o.kind}`, { defaultValue: o.label }) })),
    { kind: 'reassign_manual', label: t('resolve.manualOption') },
  ];

  const submitReassign = () => {
    if (!exception || !assignee) return;
    reassign.mutate(
      { id: exception.id, expectedVersion: version, newAssigneeId: assignee },
      { onSuccess: () => onDone('action.reassigned'), onError },
    );
  };

  const submitResolve = () => {
    if (!exception || !chosenKind) return;
    const choice = resolutionChoices.find((c) => c.kind === chosenKind);
    resolve.mutate(
      {
        id: exception.id,
        expectedVersion: version,
        chosenResolution: { kind: chosenKind, label: choice?.label ?? chosenKind },
        rationale: rationale.trim() || undefined,
      },
      {
        onSuccess: (res) => onDone(res.awaitingSecondApproval ? 'action.awaitingSecond' : 'action.resolved'),
        onError,
      },
    );
  };

  const askOsBuddy = () => {
    if (!exception) return;
    openException(exception.id, String(exception.version));
    handleClose();
  };

  const submitReopen = () => {
    if (!exception) return;
    reopen.mutate(
      { id: exception.id, expectedVersion: version, reason: reason.trim() || undefined },
      { onSuccess: () => onDone('action.reopened'), onError },
    );
  };

  const shortfall = typeof exception?.evidence?.['shortfallHours'] === 'number' ? (exception.evidence['shortfallHours'] as number) : null;
  const decisionDay = typeof exception?.evidence?.['decisionDay'] === 'string' ? (exception.evidence['decisionDay'] as string) : null;
  // The day's calculation, so the reason is visible without the database (#3407).
  const evidenceHours = (key: string): number | null =>
    typeof exception?.evidence?.[key] === 'number' ? (exception.evidence[key] as number) : null;
  const availableHours = evidenceHours('availableHours');
  const committedHours = evidenceHours('committedHours');
  const freeHours = evidenceHours('freeHours');
  const largestMechanicFreeHours = evidenceHours('largestMechanicFreeHours');

  const fmt = (iso: string) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));

  return (
    <ResponsiveDialog
      open={!!exceptionId}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      mobileTitle={exception ? t(`type.${exception.type}`, { defaultValue: exception.type }) : t('title')}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1 }}>{exception ? t(`type.${exception.type}`, { defaultValue: exception.type }) : t('title')}</Box>
        {exception && <Chip size="small" color={URGENCY_COLOR[exception.urgency]} label={t(`urgency.${exception.urgency}`)} />}
        {exception && <Chip size="small" variant="outlined" label={t(`status.${exception.status}`)} />}
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {isError && (
          <Stack spacing={1} sx={{ py: 2 }} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary">{t('error')}</Typography>
            <Button size="small" onClick={() => void refetch()}>{tc('errors.refresh')}</Button>
          </Stack>
        )}

        {exception && (
          <Stack spacing={2}>
            {/* Evidence */}
            {(decisionDay || shortfall != null) && (
              <Typography variant="body2" color="text.secondary">
                {decisionDay ? t('card.day', { day: decisionDay }) : ''}
                {decisionDay && shortfall != null ? ' · ' : ''}
                {shortfall != null ? t('card.shortfall', { hours: shortfall }) : ''}
              </Typography>
            )}
            {availableHours != null && committedHours != null && (
              <Typography variant="body2" color="text.secondary">
                {t('card.capacity', { available: availableHours, committed: committedHours, free: freeHours ?? 0 })}
              </Typography>
            )}
            {largestMechanicFreeHours != null && (
              // A job is not split between mechanics (#3523).
              <Typography variant="body2" color="text.secondary">
                {t('card.largestMechanicFree', { hours: largestMechanicFreeHours })}
              </Typography>
            )}

            {exception.type === 'email_delivery_failed' && <EmailDeliveryEvidence evidence={exception.evidence} detailed />}

            {/* Assignment */}
            <Typography variant="body2">
              {exception.assignedTo
                ? t('detail.assignedTo', { name: nameById.get(exception.assignedTo) ?? exception.assignedTo })
                : t('detail.unassigned')}
            </Typography>

            {/* Resolution options (read-only reference) */}
            {exception.resolutionOptions && exception.resolutionOptions.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">{t('card.options')}</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {exception.resolutionOptions.map((o) => (
                    <Typography key={o.kind} variant="body2">• {t(`resolution.${o.kind}`, { defaultValue: o.label })}</Typography>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />

            {/* Audit history */}
            <Box>
              <Typography variant="caption" color="text.secondary">{t('detail.history')}</Typography>
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {(data?.events ?? []).map((ev) => (
                  <Box key={ev.id} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ minWidth: 96 }}>
                      {t(`event.${ev.eventType}`, { defaultValue: ev.eventType })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ev.actorId ? (nameById.get(ev.actorId) ?? ev.actorId) : t('detail.systemActor')} · {fmt(ev.createdAt)}
                      {ev.reason ? ` · ${ev.reason}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Inline action forms */}
            {mode === 'reassign' && (
              <FormControl fullWidth size="small">
                <InputLabel>{t('reassign.assignee')}</InputLabel>
                <Select value={assignee} label={t('reassign.assignee')} onChange={(e) => setAssignee(e.target.value)}>
                  {(staff?.users ?? [])
                    .filter((u) => u.isActive && u.id !== exception.assignedTo)
                    .map((u) => (
                      <MenuItem key={u.id} value={u.id}>{`${u.firstName} ${u.lastName}`.trim() || u.email}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            {mode === 'resolve' && (
              <Stack spacing={1}>
                <FormControl>
                  <Typography variant="caption" color="text.secondary">{t('resolve.choose')}</Typography>
                  <RadioGroup value={chosenKind} onChange={(e) => setChosenKind(e.target.value)}>
                    {resolutionChoices.map((c) => (
                      <FormControlLabel key={c.kind} value={c.kind} control={<Radio size="small" />} label={c.label} />
                    ))}
                  </RadioGroup>
                </FormControl>
                <TextField
                  size="small"
                  label={t('resolve.rationale')}
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Stack>
            )}

            {mode === 'reopen' && (
              <TextField
                size="small"
                label={t('reopen.reason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {mode === 'view' && exception && (
          <>
            <Button startIcon={<SmartToyOutlinedIcon />} disabled={busy} onClick={askOsBuddy}>
              {t('action.askOsBuddy')}
            </Button>
            {showClaim && (
              <Button variant="contained" disabled={busy} onClick={() => claim.mutate({ id: exception.id }, { onSuccess: () => onDone('action.claimed'), onError })}>
                {t('action.claim')}
              </Button>
            )}
            {showRelease && (
              <Button disabled={busy} onClick={() => release.mutate({ id: exception.id, expectedVersion: version }, { onSuccess: () => onDone('action.released'), onError })}>
                {t('action.release')}
              </Button>
            )}
            {showReassign && (
              <Button disabled={busy} onClick={() => setMode('reassign')}>{t('action.reassign')}</Button>
            )}
            {showResolve && (
              <Button variant="contained" disabled={busy} onClick={() => setMode('resolve')}>{t('action.resolve')}</Button>
            )}
            {showReopen && (
              <Button disabled={busy} onClick={() => setMode('reopen')}>{t('action.reopen')}</Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={handleClose}>{tc('close')}</Button>
          </>
        )}

        {mode === 'reassign' && (
          <>
            <Button onClick={() => setMode('view')} disabled={busy}>{tc('cancel')}</Button>
            <Button variant="contained" onClick={submitReassign} disabled={busy || !assignee}>{t('action.reassign')}</Button>
          </>
        )}
        {mode === 'resolve' && (
          <>
            <Button onClick={() => setMode('view')} disabled={busy}>{tc('cancel')}</Button>
            <Button variant="contained" onClick={submitResolve} disabled={busy || !chosenKind}>{t('action.resolve')}</Button>
          </>
        )}
        {mode === 'reopen' && (
          <>
            <Button onClick={() => setMode('view')} disabled={busy}>{tc('cancel')}</Button>
            <Button variant="contained" color="warning" onClick={submitReopen} disabled={busy}>{t('action.reopen')}</Button>
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
